import NavigationPage from '@/app/components/NavigationPage'
import { Button, Card, Text } from '@/components'
import ChargeLimitSlider from '@/app/components/ChargeLimitSlider/ChargeLimitSlider'
import { getVehicleDisplayName } from '@/utils/CarUtils'
import VehiclePage from '@/app/components/VehiclePage/VehiclePage'
import useVehicleSettingsManager from '@/hooks/useVehicleSettingsManager'
import { VehicleChargeSchedule } from '@/types'
import { useState } from 'react'
import { cloneDeep } from 'lodash'
import { AlertMessage } from 'app-components'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import { canCharge } from '@/utils/vehicles/permissions'
import ChargeLimitsBar from '@/app/features/charge-forecast-v2/ChargeLimitsBar'
import {
  percentToTarget,
  targetToPercent,
} from '@/app/features/charge-forecast-v2/ChargeLimits.utils'
import { isChargeForecastV2PreviewOn } from '@/app/features/charge-forecast-v2/chargeForecastV2Preview'

const DEFAULT_MIN_PERCENT = 20
const DEFAULT_MAX_PERCENT = 80

/**
 * The Charge Forecast v2 version of the limits control: the dashboard's
 * battery bar with a handle for the minimum and one for the limit. Edits go
 * through the same onChange as the old slider, so Submit saves them the
 * same way.
 */
function ChargeLimitsBarControl({
  onChange,
}: {
  onChange: (chargeSchedule: VehicleChargeSchedule) => void
}) {
  const { isLoading, vehicle, chargeSchedule } = useVehicleSettingsManager()
  const [draft, setDraft] = useState<{ min: number; max: number }>()

  if (isLoading || !vehicle || !chargeSchedule) {
    return (
      <div className="flex flex-col space-y-4 px-1 py-2 mb-2">
        <div className="w-full">
          <div className="bg-gray-200 animate-pulse h-3" />
        </div>
      </div>
    )
  }

  const unit = chargeSchedule.battery_target_unit
  const milesPerPercent = vehicle.miles_per_battery_percent
  const saved = {
    min: targetToPercent(
      chargeSchedule.battery_target_min,
      unit,
      milesPerPercent,
      DEFAULT_MIN_PERCENT,
    ),
    max: targetToPercent(
      chargeSchedule.battery_target_max,
      unit,
      milesPerPercent,
      DEFAULT_MAX_PERCENT,
    ),
  }
  const current = draft ?? saved
  const editable = canCharge(vehicle)

  const update = (next: { min: number; max: number }) => {
    setDraft(next)
    onChange({
      ...cloneDeep(chargeSchedule),
      battery_target_min: percentToTarget(next.min, unit, milesPerPercent),
      battery_target_max: percentToTarget(next.max, unit, milesPerPercent),
    })
  }

  return (
    <ChargeLimitsBar
      batteryPercent={vehicle.battery_level_percent ?? null}
      minPercent={current.min}
      maxPercent={current.max}
      onMinChange={editable ? (min) => update({ ...current, min }) : undefined}
      onMaxChange={editable ? (max) => update({ ...current, max }) : undefined}
    />
  )
}

function VehicleLimits() {
  const { vehicle, debouncedSave } = useVehicleSettingsManager()
  const showChargeForecastV2 =
    useFeatureFlag({
      flag: 'charge_forecast_v2',
      experimentalValue: true,
      rollout: true,
    }) || isChargeForecastV2PreviewOn()
  const [localChargeSchedule, setLocalChargeSchedule] = useState<
    VehicleChargeSchedule | undefined
  >()

  const onSliderChange = (updatedChargeSchedule: VehicleChargeSchedule) => {
    const clonedSchedule = cloneDeep(updatedChargeSchedule)
    setLocalChargeSchedule({ ...clonedSchedule })
  }

  const onSubmit = () => {
    if (!localChargeSchedule) {
      return
    }

    debouncedSave(localChargeSchedule)
    setLocalChargeSchedule(undefined)
  }

  return (
    <Card>
      <Text variant={'h3'} className="mt-1">
        Adjust Charge Limits
      </Text>
      <Text variant={'body3'} className="mt-1.5">
        {getVehicleDisplayName(vehicle)}
      </Text>
      <div className={showChargeForecastV2 ? 'mt-5 mb-6' : 'mt-5'}>
        {showChargeForecastV2 ? (
          <ChargeLimitsBarControl onChange={onSliderChange} />
        ) : (
          <ChargeLimitSlider onChange={onSliderChange} />
        )}
      </div>
      <AlertMessage variant="info">
        Your car will reach your Charge Minimum upon plug-in, and reach your
        Charge Limit by departure
      </AlertMessage>
      <Button
        id={'submit-charge-schedule'}
        onClick={onSubmit}
        type="submit"
        variant="primary"
        disabled={!localChargeSchedule}
        savable={true}
        className="mt-6"
      >
        Submit
      </Button>
    </Card>
  )
}

export default function VehicleLimitsPage() {
  return (
    <NavigationPage
      id="charge-limits-page"
      title="Limits"
      subtitle="Use this to adjust your car's charge limit"
    >
      <VehiclePage>
        <VehicleLimits />
      </VehiclePage>
    </NavigationPage>
  )
}
